import { buildScheduleTripSearchRequestFromParams } from "@/features/purchase-ticket/hook/useScheduleTripSearch";
import { ModifyCard } from "@/features/purchase-ticket/select/ModifyCard";
import PortCard, {
  type PortCardInitialValues,
} from "@/features/purchase-ticket/PortCard";
import SelectTripSkeleton from "@/pages/purchase-ticket/SelectTripSkeleton";
import { usePorts } from "@/hooks/usePorts";
import { SearchTrip } from "@/services/purchase.service";
import type { ScheduleTripData } from "@/types/scheduletrip";
import type { PaxCount } from "@/features/purchase-ticket/PaxSelect";
import type { TripType } from "@/types/port";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useTdaStore } from "@/stores/tda.store";
import { hasMatchingTdaVerification } from "@/utils/tdaVerification";

export default function SelectTripPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { ticketTypeList } = usePorts();
  const verifiedTdaPassengers = useTdaStore(
    (state) => state.verifiedTdaPassengers,
  );
  const clearVerifiedTdaPassengers = useTdaStore(
    (state) => state.clearVerifiedTdaPassengers,
  );
  const searchParamsKey = searchParams.toString();
  const isVerifyTda =
    (location.state as { isVerifyTda?: boolean } | null)?.isVerifyTda === true;
  const ticketTypeKey = ticketTypeList
    .map((ticketType) => ticketType.mdTicketTypeId)
    .join(",");
  const lastSearchKey = useRef("");
  const mountedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [scheduleTripData, setScheduleTripData] = useState<ScheduleTripData | null>(null);
  const [showModify, setShowModify] = useState(false);

  const departureCount = scheduleTripData?.departureTripList?.length ?? 3;
  const returnCount = scheduleTripData?.returnTripList?.length ?? 3;

  const tripType = (searchParams.get("tripType") ?? "one-way") as TripType;
  const toDate = (value: string | null) =>
    value ? new Date(`${value}T00:00:00`) : new Date();
  const initialPax: PaxCount = {
    adultMYS: Number(searchParams.get("adultMYS") ?? 0),
    childMYS: Number(searchParams.get("childMYS") ?? 0),
    infantMYS: Number(searchParams.get("infantMYS") ?? 0),
    adultForeign: Number(searchParams.get("adultForeign") ?? 0),
    childForeign: Number(searchParams.get("childForeign") ?? 0),
    infantForeign: Number(searchParams.get("infantForeign") ?? 0),
    local: Number(searchParams.get("local") ?? 0),
    foc: Number(searchParams.get("foc") ?? 0),
    worker: Number(searchParams.get("worker") ?? 0),
  };
  const initialValues: PortCardInitialValues = {
    tripType,
    originId: searchParams.get("originId") ?? "",
    destinationId: searchParams.get("destinationId") ?? "",
    departureDate: toDate(searchParams.get("departDate")),
    returnDate: toDate(searchParams.get("returnDate")),
    pax: initialPax,
  };

  useEffect(() => {
    mountedRef.current = true;
    const requestKey = `${searchParamsKey}|${ticketTypeKey}`;

    if (ticketTypeList.length === 0 || lastSearchKey.current === requestKey) {
      return () => {
        mountedRef.current = false;
      };
    }

    lastSearchKey.current = requestKey;

    const loadSearch = async () => {
      try {
        setLoading(true);

        const payload = buildScheduleTripSearchRequestFromParams(
          new URLSearchParams(searchParamsKey),
          ticketTypeList,
        );

        const response = await SearchTrip(payload);

        if (mountedRef.current && lastSearchKey.current === requestKey) {
          const allTrips = [
            ...(response.data.departureTripList ?? []),
            ...(response.data.returnTripList ?? []),
          ];

          const tdaTickets = Array.from(
            allTrips
              .flatMap((trip) => trip.ticketTypeAvailability ?? [])
              .filter(
                (ticket) =>
                  ticket.isTDA === true && ticket.requestedQuantity > 0,
              )
              .reduce((acc, ticket) => {
                const existing = acc.get(ticket.mdTicketTypeId);

                if (
                  !existing ||
                  ticket.requestedQuantity > existing.requestedQuantity
                ) {
                  acc.set(ticket.mdTicketTypeId, ticket);
                }

                return acc;
              }, new Map<string, (typeof allTrips)[number]["ticketTypeAvailability"][number]>())
              .values(),
          );

          const hasPreviousTdaVerification = hasMatchingTdaVerification(
            tdaTickets,
            verifiedTdaPassengers,
          );

          if (tdaTickets.length === 0) {
            clearVerifiedTdaPassengers();
            setScheduleTripData(response.data);
            return;
          }

          if (!isVerifyTda && !hasPreviousTdaVerification) {
            navigate("/purchase-ticket/verify-tda", {
              state: {
                tdaTickets: tdaTickets.map((ticket) => ({
                  mdTicketTypeId: ticket.mdTicketTypeId,
                  ticketTypeName: ticket.ticketTypeName,
                  ticketCategoryName: ticket.ticketCategoryName,
                  requestedQuantity: ticket.requestedQuantity,
                })),
                searchParams: searchParamsKey,
              },
            });

            return;
          }

          setScheduleTripData(response.data);
        }
      } catch (error) {
        if (mountedRef.current && lastSearchKey.current === requestKey) {
          toast.error("Failed to load trip information.");
          console.error(error);
        }
      } finally {
        if (mountedRef.current && lastSearchKey.current === requestKey) {
          setLoading(false);
        }
      }
    };

    loadSearch();

    return () => {
      mountedRef.current = false;
    };
  }, [
    clearVerifiedTdaPassengers,
    isVerifyTda,
    navigate,
    searchParamsKey,
    ticketTypeKey,
    ticketTypeList,
    verifiedTdaPassengers,
  ]);

  const handleModify = () => {
    setShowModify((current) => !current);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <Button
        type="button"
        size="lg"
        className={`mt-4 mb-8 ${showModify ? "w-auto" : "w-28"} border border-muted-foreground bg-background text-muted-foreground cursor-pointer shadow-md hover:bg-primary-soft`}
        onClick={handleModify}
        aria-label={showModify ? "Close modify form" : "Modify search"}
      >
        {showModify ? <X size={20} aria-hidden="true" /> : "Modify"}
      </Button>

      {showModify && (
        <PortCard
          initialValues={initialValues}
          submitLabel="Update"
          onSubmitted={() => setShowModify(false)}
        />
      )}

      <h1 className="text-secondary-foreground text-lg font-semibold">
        Itinerary
      </h1>

      {loading ? (
        <SelectTripSkeleton
          departureCount={departureCount}
          returnCount={returnCount}
        />
      ) : scheduleTripData ? (
        <ModifyCard scheduleTripData={scheduleTripData} />
      ) : (
        <p className="text-sm text-muted-foreground">
          Unable to load trip information.
        </p>
      )}
    </div>
  );
}
